import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, ExternalLink, Eye, EyeOff, MousePointerClick } from 'lucide-react';
import trackReferralClick from '../../utils/referralTracker';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ReferralsTab — admin-only hub for managing outbound referral / affiliate
 * links (Chime seed today; Cash App, Uber, Airbnb, etc. later). Each entry
 * captures the partner name, offer text, FTC disclosure, URL, active state,
 * and per-partner public visibility toggle. Public consumers fetch via
 * GET /api/referrals/public — this admin tab is behind /api/admin/referrals.
 */
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return { Authorization: `Bearer ${t}` };
};

const emptyForm = {
  partner_name: '',
  offer_text: '',
  disclosure: 'Referral link — I may earn a bonus if you sign up and qualify. Terms apply.',
  url: '',
  is_active: true,
  is_public: false,
  display_order: 0,
  icon_hint: '',
  notes: '',
};

const ReferralsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/referrals`, { headers: authHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      partner_name: row.partner_name || '',
      offer_text: row.offer_text || '',
      disclosure: row.disclosure || emptyForm.disclosure,
      url: row.url || '',
      is_active: !!row.is_active,
      is_public: !!row.is_public,
      display_order: row.display_order || 0,
      icon_hint: row.icon_hint || '',
      notes: row.notes || '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.partner_name.trim() || !form.offer_text.trim() || !form.url.trim()) {
      setError('Partner name, offer text, and URL are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/referrals/${editing.referral_id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/api/admin/referrals`, form, { headers: authHeaders() });
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete referral "${row.partner_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/referrals/${row.referral_id}`, { headers: authHeaders() });
      await load();
    } catch (e) {
      window.alert(`Delete failed: ${(e.response && e.response.data && e.response.data.detail) || e.message}`);
    }
  };

  return (
    <div data-testid="referrals-tab">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <div>
          <h2 className="font-['Bebas_Neue'] text-2xl">REFERRAL PROGRAMS</h2>
          <p className="text-xs text-[#A3A3A3] mt-1 max-w-2xl">
            Manage your outbound referral / affiliate links. Toggle &ldquo;Public&rdquo; to expose an entry on the public referral surface (disclosure always renders alongside).
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="referral-add-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
        >
          <Plus size={18} />
          Add Referral
        </button>
      </div>

      {loading ? (
        <div className="text-[#A3A3A3] text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[#A3A3A3] text-sm border border-dashed border-white/10 rounded-lg p-8 text-center">
          No referral programs yet. Click &ldquo;Add Referral&rdquo; to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((r) => (
            <div
              key={r.referral_id}
              data-testid={`referral-card-${r.referral_id}`}
              className={`rounded-xl p-5 border ${r.is_active ? 'border-white/10 bg-white/[0.03]' : 'border-white/5 bg-black/40 opacity-60'} flex flex-col gap-3`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">{r.partner_name}</p>
                  <p className="mt-1 text-white font-medium leading-snug">{r.offer_text}</p>
                </div>
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
                  {r.is_public ? (
                    <span className="flex items-center gap-1 text-green-400" title="Visible on public referral surface">
                      <Eye size={12} /> Public
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[#737373]" title="Admin only">
                      <EyeOff size={12} /> Private
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[10px] leading-tight text-[#737373] italic">{r.disclosure}</p>
              <div
                className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3]"
                data-testid={`referral-clicks-${r.referral_id}`}
              >
                <span className="flex items-center gap-1">
                  <MousePointerClick size={12} className="text-[#D4AF37]" />
                  <span className="text-white font-semibold">{r.clicks_30d ?? 0}</span>
                  <span>clicks · 30d</span>
                </span>
                <span className="text-[#525252]">
                  All-time: <span className="text-[#A3A3A3]">{r.clicks_total ?? 0}</span>
                </span>
              </div>
              <div className="flex items-center gap-2 mt-auto pt-3 border-t border-white/5">
                <a
                  href={r.url}
                  target="_blank"
                  rel="nofollow sponsored noopener noreferrer"
                  data-testid={`referral-open-${r.referral_id}`}
                  onClick={() => trackReferralClick(r.referral_id)}
                  onAuxClick={() => trackReferralClick(r.referral_id)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-black border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors rounded"
                >
                  <ExternalLink size={13} /> Open Link
                </a>
                <button
                  onClick={() => openEdit(r)}
                  data-testid={`referral-edit-${r.referral_id}`}
                  className="p-2 border border-white/10 text-[#A3A3A3] hover:text-white hover:border-white/30 transition-colors rounded"
                  title="Edit"
                >
                  <Edit3 size={14} />
                </button>
                <button
                  onClick={() => remove(r)}
                  data-testid={`referral-delete-${r.referral_id}`}
                  className="p-2 border border-white/10 text-[#A3A3A3] hover:text-red-400 hover:border-red-400/40 transition-colors rounded"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {r.notes && (
                <p className="text-[10px] text-[#525252] mt-1">📝 {r.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          data-testid="referral-modal"
        >
          <div className="w-full max-w-lg bg-[#0D0D0D] border border-white/10 rounded-xl p-6 space-y-4">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white">
              {editing ? 'EDIT REFERRAL' : 'ADD REFERRAL'}
            </h3>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Partner name *</label>
              <input
                type="text"
                value={form.partner_name}
                onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                placeholder="e.g. Chime, Cash App, Uber"
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                data-testid="referral-input-partner-name"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Offer text *</label>
              <input
                type="text"
                value={form.offer_text}
                onChange={(e) => setForm({ ...form, offer_text: e.target.value })}
                placeholder="e.g. Get $100 with Chime"
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                data-testid="referral-input-offer-text"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Disclosure (FTC)</label>
              <textarea
                value={form.disclosure}
                onChange={(e) => setForm({ ...form, disclosure: e.target.value })}
                rows={2}
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded text-sm"
                data-testid="referral-input-disclosure"
              />
              <p className="text-[10px] text-[#525252] mt-1">Rendered directly beneath the button on every surface. Must be visible before the click.</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Referral URL *</label>
              <input
                type="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://…"
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded font-mono text-sm"
                data-testid="referral-input-url"
              />
              <p className="text-[10px] text-[#525252] mt-1">Outbound link. Rendered with rel=&quot;nofollow sponsored noopener noreferrer&quot;.</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="referral-input-active"
                />
                <span className="text-white">Active</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                  data-testid="referral-input-public"
                />
                <span className="text-white">Public</span>
              </label>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Order</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })}
                  className="w-full mt-1 px-2 py-1 bg-black border border-white/10 text-white rounded text-sm"
                  data-testid="referral-input-order"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Private notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Internal reminders (never rendered to users)."
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded text-sm"
                data-testid="referral-input-notes"
              />
            </div>

            {error && (
              <div className="text-sm text-red-400" data-testid="referral-error">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border border-white/10 text-[#A3A3A3] hover:text-white text-sm uppercase tracking-wider rounded"
                data-testid="referral-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-[#D4AF37] text-black text-sm uppercase tracking-wider hover:bg-[#E5C048] transition-colors rounded disabled:opacity-50"
                data-testid="referral-modal-save"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create referral'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralsTab;
